import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OrganizationLayout from './OrganizationLayout';
import { useOrgData, API, authHeaders } from './useOrgData';

const PRIORITY_STYLE = {
  High: 'bg-red-50 text-red-700 border-red-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const formatDue = (t) => {
  if (!t.due_date) return '—';
  const d = new Date(`${String(t.due_date).slice(0, 10)}T00:00:00`);
  return `${d.toLocaleDateString()}${t.time ? ` · ${t.time}` : ''}`;
};

const TaskRow = ({ task, ownerName, onOpen }) => (
  <tr
    onClick={onOpen}
    className="cursor-pointer hover:bg-indigo-50/40 transition-colors"
    data-testid={`team-task-row-${task.id}`}
  >
    <td className="px-4 py-3">
      <p className={`font-medium text-gray-900 ${task.completed ? 'line-through text-gray-400' : ''}`}>{task.title}</p>
      {task.category && <p className="text-xs text-gray-500">{task.category}</p>}
    </td>
    <td className="px-4 py-3 text-sm text-gray-700 hidden sm:table-cell">{ownerName}</td>
    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDue(task)}</td>
    <td className="px-4 py-3 hidden md:table-cell">
      {task.priority && (
        <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${PRIORITY_STYLE[task.priority.charAt(0).toUpperCase() + task.priority.slice(1).toLowerCase()] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
          {task.priority}
        </span>
      )}
    </td>
    <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell capitalize">{task.visibility || 'team'}</td>
  </tr>
);

const OrganizationTeamTasksPage = () => {
  const navigate = useNavigate();
  const { org, members, loading, canManage } = useOrgData();
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    if (!org) return;
    // fetch (not axios) so the workspace interceptor can't swap the org_id.
    fetch(`${API}/tasks?org_id=${org.id}&all=true`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setTasks(Array.isArray(data) ? data : []))
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false));
  }, [org]);

  const nameOf = useMemo(() => {
    const map = Object.fromEntries(members.map((m) => [m.user_id, m.name || m.email]));
    return (id) => map[id] || 'Former member';
  }, [members]);

  const visible = tasks
    .filter((t) => showCompleted || !t.completed)
    .filter((t) => ownerFilter === 'all' || t.user_id === ownerFilter)
    .sort((a, b) => String(a.due_date || '9999').localeCompare(String(b.due_date || '9999')));

  if (loading) return null;
  if (!org) return null;

  return (
    <OrganizationLayout org={org} active="tasks" canManage={canManage}>
      <div data-testid="org-team-tasks-page">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team tasks</h1>
            <p className="text-sm text-gray-500">{visible.length} task{visible.length === 1 ? '' : 's'} shown</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              data-testid="team-tasks-owner-filter"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">Everyone</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
              ))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                data-testid="team-tasks-show-completed"
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show completed
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {tasksLoading ? (
            <p className="p-6 text-sm text-gray-500">Loading tasks…</p>
          ) : visible.length === 0 ? (
            <p className="p-6 text-sm text-gray-500" data-testid="team-tasks-empty">No team tasks match these filters.</p>
          ) : (
            <table className="w-full text-left" data-testid="team-tasks-table">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Task</th>
                  <th className="px-4 py-3 font-semibold hidden sm:table-cell">Owner</th>
                  <th className="px-4 py-3 font-semibold">Due</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Priority</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Visibility</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((t) => (
                  <TaskRow key={t.id} task={t} ownerName={nameOf(t.user_id)} onOpen={() => navigate(`/tasks/${t.id}`)} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OrganizationLayout>
  );
};

export default OrganizationTeamTasksPage;
